// Comprehensive South African legislation library shown on /legislation.
// Each entry links to the official source on www.gov.za where possible.

export type ActEntry = {
  title: string;
  short?: string;
  year: number;
  number: string; // e.g., "66 of 1995"
  category: string;
  summary: string;
  url?: string;
};

/**
 * Build a search URL on lawlibrary.org.za for an Act. LawLibrary republishes
 * consolidated South African legislation with amendments applied, which makes
 * it the best public source for "latest version" of an Act. We link out for
 * amendment history rather than scrape (site is Cloudflare-protected).
 */
export function lawLibraryUrl(a: Pick<ActEntry, "title" | "short">) {
  const q = encodeURIComponent(a.short ?? a.title);
  return `https://lawlibrary.org.za/search/?q=${q}&doc_type=Legislation`;
}

/** Browse recent SA judgments / Acts on LawLibrary (AfricanLII). */
export const LAW_LIBRARY_RECENT = {
  judgments: "https://lawlibrary.org.za/judgments/",
  legislation: "https://lawlibrary.org.za/legislation/",
  home: "https://lawlibrary.org.za/",
} as const;

export const LEGISLATION_CATEGORIES = [
  "Constitutional",
  "Labour & Employment",
  "Consumer & Commercial",
  "Data & Cyber",
  "Criminal & Procedural",
  "Family & Social",
  "Property & Housing",
  "Administrative & Public",
  "Tax & Financial",
] as const;

export const LEGISLATION: ActEntry[] = [
  // Constitutional
  {
    title: "Constitution of the Republic of South Africa, 1996",
    short: "Constitution",
    year: 1996,
    number: "108 of 1996",
    category: "Constitutional",
    summary:
      "Supreme law of the Republic. Establishes the Bill of Rights, the three spheres of government, courts and chapter 9 institutions.",
    url: "https://www.gov.za/documents/constitution-republic-south-africa-1996",
  },

  // Labour & Employment
  {
    title: "Labour Relations Act, 1995",
    short: "LRA",
    year: 1995,
    number: "66 of 1995",
    category: "Labour & Employment",
    summary:
      "Governs collective bargaining, trade unions, strikes, the CCMA, Labour Court, unfair dismissal and unfair labour practices.",
    url: "https://www.gov.za/documents/labour-relations-act",
  },
  {
    title: "Basic Conditions of Employment Act, 1997",
    short: "BCEA",
    year: 1997,
    number: "75 of 1997",
    category: "Labour & Employment",
    summary:
      "Sets minimum working conditions: hours, overtime, leave (annual, sick, family, maternity), payslips, termination notice.",
    url: "https://www.gov.za/documents/basic-conditions-employment-act",
  },
  {
    title: "Employment Equity Act, 1998",
    short: "EEA",
    year: 1998,
    number: "55 of 1998",
    category: "Labour & Employment",
    summary:
      "Prohibits unfair discrimination in employment and requires designated employers to implement affirmative action plans.",
    url: "https://www.gov.za/documents/employment-equity-act",
  },
  {
    title: "Skills Development Act, 1998",
    short: "SDA",
    year: 1998,
    number: "97 of 1998",
    category: "Labour & Employment",
    summary:
      "Creates SETAs, learnerships and the National Skills Authority to develop the South African workforce.",
    url: "https://www.gov.za/documents/skills-development-act",
  },
  {
    title: "Occupational Health and Safety Act, 1993",
    short: "OHSA",
    year: 1993,
    number: "85 of 1993",
    category: "Labour & Employment",
    summary:
      "Requires employers to provide and maintain a safe working environment. Establishes duties of employers, employees and inspectors.",
    url: "https://www.gov.za/documents/occupational-health-and-safety-act",
  },
  {
    title: "Compensation for Occupational Injuries and Diseases Act, 1993",
    short: "COIDA",
    year: 1993,
    number: "130 of 1993",
    category: "Labour & Employment",
    summary:
      "No-fault compensation for employees injured or contracting diseases in the course of employment.",
    url: "https://www.gov.za/documents/compensation-occupational-injuries-and-diseases-act",
  },
  {
    title: "Unemployment Insurance Act, 2001",
    short: "UIA",
    year: 2001,
    number: "63 of 2001",
    category: "Labour & Employment",
    summary:
      "Provides UIF benefits for unemployment, illness, maternity, adoption and the dependants of deceased contributors.",
    url: "https://www.gov.za/documents/unemployment-insurance-act",
  },
  {
    title: "National Minimum Wage Act, 2018",
    short: "NMWA",
    year: 2018,
    number: "9 of 2018",
    category: "Labour & Employment",
    summary:
      "Establishes the National Minimum Wage, the NMW Commission and review process for annual adjustments.",
    url: "https://www.gov.za/documents/national-minimum-wage-act-9-2018-english-afrikaans-27-nov-2018-0000",
  },
  {
    title: "Protected Disclosures Act, 2000",
    short: "PDA",
    year: 2000,
    number: "26 of 2000",
    category: "Labour & Employment",
    summary:
      "Protects whistleblowers in the workplace from occupational detriment after disclosing wrongdoing.",
    url: "https://www.gov.za/documents/protected-disclosures-act",
  },

  // Consumer & Commercial
  {
    title: "Consumer Protection Act, 2008",
    short: "CPA",
    year: 2008,
    number: "68 of 2008",
    category: "Consumer & Commercial",
    summary:
      "Codifies consumer rights, cooling-off, product liability, unfair business practices and the National Consumer Tribunal.",
    url: "https://www.gov.za/documents/consumer-protection-act",
  },
  {
    title: "National Credit Act, 2005",
    short: "NCA",
    year: 2005,
    number: "34 of 2005",
    category: "Consumer & Commercial",
    summary:
      "Regulates credit providers, reckless lending, debt review, prescription of debt and the National Credit Regulator.",
    url: "https://www.gov.za/documents/national-credit-act",
  },
  {
    title: "Companies Act, 2008",
    short: "Companies Act",
    year: 2008,
    number: "71 of 2008",
    category: "Consumer & Commercial",
    summary:
      "Incorporation, governance and winding-up of companies. Director duties, business rescue and the Companies Tribunal.",
    url: "https://www.gov.za/documents/companies-act",
  },
  {
    title: "Close Corporations Act, 1984",
    year: 1984,
    number: "69 of 1984",
    category: "Consumer & Commercial",
    summary:
      "Governs existing close corporations. No new CCs may be registered after the 2008 Companies Act.",
    url: "https://www.gov.za/documents/close-corporations-act",
  },
  {
    title: "Electronic Communications and Transactions Act, 2002",
    short: "ECTA",
    year: 2002,
    number: "25 of 2002",
    category: "Consumer & Commercial",
    summary:
      "Recognises electronic signatures, e-contracts, consumer protection online and cybercrime offences.",
    url: "https://www.gov.za/documents/electronic-communications-and-transactions-act",
  },
  {
    title: "Competition Act, 1998",
    year: 1998,
    number: "89 of 1998",
    category: "Consumer & Commercial",
    summary:
      "Prohibits restrictive practices, abuse of dominance and unnotified mergers. Establishes the Competition Commission and Tribunal.",
    url: "https://www.gov.za/documents/competition-act",
  },
  {
    title: "Prescription Act, 1969",
    year: 1969,
    number: "68 of 1969",
    category: "Consumer & Commercial",
    summary:
      "Extinguishes debts after the lapse of the prescribed period (commonly 3 years for ordinary debts).",
    url: "https://www.gov.za/documents/prescription-act",
  },
  {
    title: "Insolvency Act, 1936",
    year: 1936,
    number: "24 of 1936",
    category: "Consumer & Commercial",
    summary:
      "Sequestration of individual estates, voidable dispositions and the rights of creditors.",
    url: "https://www.gov.za/documents/insolvency-act",
  },

  // Data & Cyber
  {
    title: "Protection of Personal Information Act, 2013",
    short: "POPIA",
    year: 2013,
    number: "4 of 2013",
    category: "Data & Cyber",
    summary:
      "Sets the eight conditions for lawful processing of personal information. Establishes the Information Regulator.",
    url: "https://www.gov.za/documents/protection-personal-information-act",
  },
  {
    title: "Promotion of Access to Information Act, 2000",
    short: "PAIA",
    year: 2000,
    number: "2 of 2000",
    category: "Data & Cyber",
    summary:
      "Gives effect to s 32 of the Constitution — the right of access to records held by public and private bodies.",
    url: "https://www.gov.za/documents/promotion-access-information-act",
  },
  {
    title: "Cybercrimes Act, 2020",
    year: 2020,
    number: "19 of 2020",
    category: "Data & Cyber",
    summary:
      "Creates cybercrime offences (hacking, unlawful interception, data interference, malicious communications) and victim protection orders.",
    url: "https://www.gov.za/documents/cybercrimes-act-19-2020-1-jun-2021-0000",
  },
  {
    title: "Regulation of Interception of Communications Act, 2002",
    short: "RICA",
    year: 2002,
    number: "70 of 2002",
    category: "Data & Cyber",
    summary:
      "Regulates the interception of communications and SIM-card registration.",
    url: "https://www.gov.za/documents/regulation-interception-communications-and-provision-communication-related",
  },

  // Criminal & Procedural
  {
    title: "Criminal Procedure Act, 1977",
    short: "CPA (Criminal)",
    year: 1977,
    number: "51 of 1977",
    category: "Criminal & Procedural",
    summary:
      "Arrest, bail, charge sheets, plea procedures, trial, sentencing and appeals in criminal matters.",
    url: "https://www.gov.za/documents/criminal-procedure-act",
  },
  {
    title: "Criminal Law (Sexual Offences and Related Matters) Amendment Act, 2007",
    short: "Sexual Offences Act",
    year: 2007,
    number: "32 of 2007",
    category: "Criminal & Procedural",
    summary:
      "Codifies sexual offences, the National Register for Sex Offenders and victim protections.",
    url: "https://www.gov.za/documents/criminal-law-sexual-offences-and-related-matters-amendment-act",
  },
  {
    title: "Domestic Violence Act, 1998",
    short: "DVA",
    year: 1998,
    number: "116 of 1998",
    category: "Criminal & Procedural",
    summary:
      "Establishes protection orders against domestic abuse, including emotional, economic and verbal abuse.",
    url: "https://www.gov.za/documents/domestic-violence-act",
  },
  {
    title: "Protection from Harassment Act, 2011",
    year: 2011,
    number: "17 of 2011",
    category: "Criminal & Procedural",
    summary:
      "Affords victims of harassment (including cyber-harassment) the right to apply for a protection order.",
    url: "https://www.gov.za/documents/protection-harassment-act",
  },
  {
    title: "Promotion of Equality and Prevention of Unfair Discrimination Act, 2000",
    short: "PEPUDA",
    year: 2000,
    number: "4 of 2000",
    category: "Criminal & Procedural",
    summary:
      "Establishes the Equality Courts and prohibits unfair discrimination, hate speech and harassment outside the workplace.",
    url: "https://www.gov.za/documents/promotion-equality-and-prevention-unfair-discrimination-act",
  },
  {
    title: "Child Justice Act, 2008",
    year: 2008,
    number: "75 of 2008",
    category: "Criminal & Procedural",
    summary:
      "Separate criminal justice process for children in conflict with the law, including diversion.",
    url: "https://www.gov.za/documents/child-justice-act",
  },
  {
    title: "Prevention of Organised Crime Act, 1998",
    short: "POCA",
    year: 1998,
    number: "121 of 1998",
    category: "Criminal & Procedural",
    summary:
      "Targets racketeering, money laundering and criminal gang activities. Provides for asset forfeiture.",
    url: "https://www.gov.za/documents/prevention-organised-crime-act",
  },

  // Family & Social
  {
    title: "Children's Act, 2005",
    year: 2005,
    number: "38 of 2005",
    category: "Family & Social",
    summary:
      "Parental responsibilities, care and contact, child protection, foster care and adoption.",
    url: "https://www.gov.za/documents/childrens-act",
  },
  {
    title: "Maintenance Act, 1998",
    year: 1998,
    number: "99 of 1998",
    category: "Family & Social",
    summary:
      "Procedure for obtaining and enforcing maintenance orders in the Maintenance Court.",
    url: "https://www.gov.za/documents/maintenance-act",
  },
  {
    title: "Divorce Act, 1979",
    year: 1979,
    number: "70 of 1979",
    category: "Family & Social",
    summary:
      "Grounds for divorce, division of assets and forfeiture of patrimonial benefits.",
    url: "https://www.gov.za/documents/divorce-act",
  },
  {
    title: "Recognition of Customary Marriages Act, 1998",
    year: 1998,
    number: "120 of 1998",
    category: "Family & Social",
    summary:
      "Recognises customary marriages and regulates their consequences and registration.",
    url: "https://www.gov.za/documents/recognition-customary-marriages-act",
  },
  {
    title: "Civil Union Act, 2006",
    year: 2006,
    number: "17 of 2006",
    category: "Family & Social",
    summary:
      "Provides for the solemnisation and registration of civil unions between two persons.",
    url: "https://www.gov.za/documents/civil-union-act",
  },
  {
    title: "Social Assistance Act, 2004",
    year: 2004,
    number: "13 of 2004",
    category: "Family & Social",
    summary:
      "Establishes the legal basis for social grants administered by SASSA.",
    url: "https://www.gov.za/documents/social-assistance-act",
  },

  // Property & Housing
  {
    title: "Rental Housing Act, 1999",
    year: 1999,
    number: "50 of 1999",
    category: "Property & Housing",
    summary:
      "Governs the rental housing market, lease obligations and the Rental Housing Tribunal.",
    url: "https://www.gov.za/documents/rental-housing-act",
  },
  {
    title: "Sectional Titles Act, 1986",
    year: 1986,
    number: "95 of 1986",
    category: "Property & Housing",
    summary:
      "Regulates ownership of units in sectional title schemes.",
    url: "https://www.gov.za/documents/sectional-titles-act",
  },
  {
    title: "Prevention of Illegal Eviction from and Unlawful Occupation of Land Act, 1998",
    short: "PIE Act",
    year: 1998,
    number: "19 of 1998",
    category: "Property & Housing",
    summary:
      "Sets a court-supervised process for evictions, balancing landowner rights with constitutional housing protections.",
    url: "https://www.gov.za/documents/prevention-illegal-eviction-and-unlawful-occupation-land-act",
  },
  {
    title: "Deeds Registries Act, 1937",
    year: 1937,
    number: "47 of 1937",
    category: "Property & Housing",
    summary:
      "Administration of deeds offices and registration of rights in immovable property.",
    url: "https://www.gov.za/documents/deeds-registries-act",
  },

  // Administrative & Public
  {
    title: "Promotion of Administrative Justice Act, 2000",
    short: "PAJA",
    year: 2000,
    number: "3 of 2000",
    category: "Administrative & Public",
    summary:
      "Codifies the right to lawful, reasonable and procedurally fair administrative action under s 33 of the Constitution.",
    url: "https://www.gov.za/documents/promotion-administrative-justice-act",
  },
  {
    title: "Public Finance Management Act, 1999",
    short: "PFMA",
    year: 1999,
    number: "1 of 1999",
    category: "Administrative & Public",
    summary:
      "Governs financial management in national and provincial government and entities.",
    url: "https://www.gov.za/documents/public-finance-management-act",
  },
  {
    title: "Municipal Finance Management Act, 2003",
    short: "MFMA",
    year: 2003,
    number: "56 of 2003",
    category: "Administrative & Public",
    summary:
      "Financial management framework for municipalities and municipal entities.",
    url: "https://www.gov.za/documents/local-government-municipal-finance-management-act",
  },
  {
    title: "Local Government: Municipal Systems Act, 2000",
    year: 2000,
    number: "32 of 2000",
    category: "Administrative & Public",
    summary:
      "Core principles for municipalities' internal systems, performance management and service delivery.",
    url: "https://www.gov.za/documents/local-government-municipal-systems-act",
  },
  {
    title: "Prevention and Combating of Corrupt Activities Act, 2004",
    short: "PRECCA",
    year: 2004,
    number: "12 of 2004",
    category: "Administrative & Public",
    summary:
      "General offence of corruption and specific offences relating to public officers, foreign officials and the private sector.",
    url: "https://www.gov.za/documents/prevention-and-combating-corrupt-activities-act",
  },

  // Tax & Financial
  {
    title: "Income Tax Act, 1962",
    year: 1962,
    number: "58 of 1962",
    category: "Tax & Financial",
    summary:
      "Principal Act governing income tax, capital gains tax and dividends tax in South Africa.",
    url: "https://www.gov.za/documents/income-tax-act",
  },
  {
    title: "Value-Added Tax Act, 1991",
    year: 1991,
    number: "89 of 1991",
    category: "Tax & Financial",
    summary:
      "Imposes VAT on the supply of goods and services and the importation of goods.",
    url: "https://www.gov.za/documents/value-added-tax-act",
  },
  {
    title: "Tax Administration Act, 2011",
    year: 2011,
    number: "28 of 2011",
    category: "Tax & Financial",
    summary:
      "Single administrative framework for all tax Acts: assessments, objections, appeals, penalties and SARS powers.",
    url: "https://www.gov.za/documents/tax-administration-act",
  },
  {
    title: "Financial Intelligence Centre Act, 2001",
    short: "FICA",
    year: 2001,
    number: "38 of 2001",
    category: "Tax & Financial",
    summary:
      "Anti-money laundering and counter-terror-financing duties for accountable institutions.",
    url: "https://www.gov.za/documents/financial-intelligence-centre-act",
  },

  // Additional Labour & Employment
  {
    title: "Labour Relations Amendment Act, 2014",
    year: 2014,
    number: "6 of 2014",
    category: "Labour & Employment",
    summary:
      "Tightens regulation of temporary employment services (labour brokers), fixed-term contracts and part-time employees earning below the BCEA threshold.",
    url: "https://www.gov.za/documents/labour-relations-amendment-act",
  },
  {
    title: "Employment Services Act, 2014",
    short: "ESA",
    year: 2014,
    number: "4 of 2014",
    category: "Labour & Employment",
    summary:
      "Establishes public employment services, regulates private employment agencies and the employment of foreign nationals.",
    url: "https://www.gov.za/documents/employment-services-act",
  },

  // Additional Consumer & Commercial
  {
    title: "Financial Advisory and Intermediary Services Act, 2002",
    short: "FAIS",
    year: 2002,
    number: "37 of 2002",
    category: "Consumer & Commercial",
    summary:
      "Regulates the rendering of financial advice and intermediary services. Establishes the FAIS Ombud.",
    url: "https://www.gov.za/documents/financial-advisory-and-intermediary-services-act",
  },
  {
    title: "Long-term Insurance Act, 1998",
    year: 1998,
    number: "52 of 1998",
    category: "Consumer & Commercial",
    summary:
      "Regulates long-term insurers, intermediaries and policy holder protection rules.",
    url: "https://www.gov.za/documents/long-term-insurance-act",
  },
  {
    title: "Short-term Insurance Act, 1998",
    year: 1998,
    number: "53 of 1998",
    category: "Consumer & Commercial",
    summary:
      "Regulates short-term insurers, brokers and policy holder protection rules.",
    url: "https://www.gov.za/documents/short-term-insurance-act",
  },
  {
    title: "Pension Funds Act, 1956",
    year: 1956,
    number: "24 of 1956",
    category: "Consumer & Commercial",
    summary:
      "Registration and governance of pension funds, the Pension Funds Adjudicator and member protections.",
    url: "https://www.gov.za/documents/pension-funds-act",
  },

  // Additional Criminal & Procedural
  {
    title: "Firearms Control Act, 2000",
    year: 2000,
    number: "60 of 2000",
    category: "Criminal & Procedural",
    summary:
      "Licensing, possession, dealing and use of firearms; competency certificates and renewals.",
    url: "https://www.gov.za/documents/firearms-control-act",
  },
  {
    title: "Drugs and Drug Trafficking Act, 1992",
    year: 1992,
    number: "140 of 1992",
    category: "Criminal & Procedural",
    summary:
      "Criminalises dealing in and use of dependence-producing substances and provides for asset forfeiture.",
    url: "https://www.gov.za/documents/drugs-and-drug-trafficking-act",
  },
  {
    title: "National Road Traffic Act, 1996",
    short: "NRTA",
    year: 1996,
    number: "93 of 1996",
    category: "Criminal & Procedural",
    summary:
      "Driver fitness, registration of vehicles, road traffic offences (including drunk driving) and accident reporting.",
    url: "https://www.gov.za/documents/national-road-traffic-act",
  },
  {
    title: "Prevention and Combating of Trafficking in Persons Act, 2013",
    year: 2013,
    number: "7 of 2013",
    category: "Criminal & Procedural",
    summary:
      "Criminalises human trafficking, provides for victim protection, identification and return.",
    url: "https://www.gov.za/documents/prevention-and-combating-trafficking-persons-act",
  },

  // Additional Family & Social
  {
    title: "Older Persons Act, 2006",
    year: 2006,
    number: "13 of 2006",
    category: "Family & Social",
    summary:
      "Protects the rights of older persons, regulates community-based care and prohibits abuse.",
    url: "https://www.gov.za/documents/older-persons-act",
  },
  {
    title: "Mental Health Care Act, 2002",
    year: 2002,
    number: "17 of 2002",
    category: "Family & Social",
    summary:
      "Procedures for voluntary, assisted and involuntary mental health care and the rights of mental health care users.",
    url: "https://www.gov.za/documents/mental-health-care-act",
  },
  {
    title: "Choice on Termination of Pregnancy Act, 1996",
    year: 1996,
    number: "92 of 1996",
    category: "Family & Social",
    summary:
      "Regulates the circumstances under which a pregnancy may be terminated lawfully in South Africa.",
    url: "https://www.gov.za/documents/choice-termination-pregnancy-act",
  },

  // Additional Property & Housing
  {
    title: "Sectional Titles Schemes Management Act, 2011",
    short: "STSMA",
    year: 2011,
    number: "8 of 2011",
    category: "Property & Housing",
    summary:
      "Governs the management of sectional title schemes, bodies corporate and the Community Schemes Ombud.",
    url: "https://www.gov.za/documents/sectional-titles-schemes-management-act",
  },
  {
    title: "Community Schemes Ombud Service Act, 2011",
    short: "CSOS Act",
    year: 2011,
    number: "9 of 2011",
    category: "Property & Housing",
    summary:
      "Establishes the CSOS to resolve disputes in sectional title, share block, home owners' associations and similar schemes.",
    url: "https://www.gov.za/documents/community-schemes-ombud-service-act",
  },
  {
    title: "Extension of Security of Tenure Act, 1997",
    short: "ESTA",
    year: 1997,
    number: "62 of 1997",
    category: "Property & Housing",
    summary:
      "Protects occupiers of rural and peri-urban land from arbitrary eviction.",
    url: "https://www.gov.za/documents/extension-security-tenure-act",
  },

  // Additional Administrative & Public
  {
    title: "Immigration Act, 2002",
    year: 2002,
    number: "13 of 2002",
    category: "Administrative & Public",
    summary:
      "Entry into and departure from South Africa, visas, permanent residence and the deportation of illegal foreigners.",
    url: "https://www.gov.za/documents/immigration-act",
  },
  {
    title: "Refugees Act, 1998",
    year: 1998,
    number: "130 of 1998",
    category: "Administrative & Public",
    summary:
      "Application for and granting of refugee status, rights and obligations of refugees and asylum seekers.",
    url: "https://www.gov.za/documents/refugees-act",
  },
  {
    title: "Identification Act, 1997",
    year: 1997,
    number: "68 of 1997",
    category: "Administrative & Public",
    summary:
      "Establishes the National Population Register and the issuing of identity documents and smart ID cards.",
    url: "https://www.gov.za/documents/identification-act",
  },
];
